import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  MenuItem,
  Toolbar,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MenuIcon from '@mui/icons-material/Menu';

const StyledButton = styled(Button)(({ theme }) => ({
  fontSize: '1.125rem',
  [theme.breakpoints.up('md')]: {
    fontSize: '1.25rem',
  },
  padding: '4px 8px',
  '&.active': {
    backgroundColor: 'rgba(0, 0, 0, 0.04);',
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  color: '#1976d2',
  '&.MuiButtonBase-root': { textTransform: 'uppercase' },
  '&.active': {
    backgroundColor: 'rgba(0, 0, 0, 0.04);',
  },
  '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.26)' },
}));

export default function NavigationBar() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPage, setSelectedPage] = useState('');
  const [toggleOpen, setToggleOpen] = useState(false);

  const user = useSelector((state) => state.users.user);

  const isLoggedIn =
    useSelector((state) => state.users.isLoggedIn) &&
    localStorage.getItem('token') !== null;

  const iconButtonRef = useRef(null);
  const linkRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  const toggleDrawer = (newOpen) => () => {
    setToggleOpen(newOpen);
  };
  const selectPage = async (page) => {
    setSelectedPage(page);
  };

  useEffect(() => {
    if (iconButtonRef.current && linkRef.current) {
      const iconButtonWidth = iconButtonRef.current.offsetWidth;
      linkRef.current.style.marginLeft = `${iconButtonWidth / 2}px`;
      linkRef.current.style.marginRight = `${iconButtonWidth / 2}px`;
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 600) {
        setAnchorEl(null);
      }
    };

    window.addEventListener('resize', handleResize);

    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (location.pathname === '/profile') {
      setSelectedPage('profile');
    } else if (location.pathname === '/agreement') {
      setSelectedPage('agreement');
    } else if (location.pathname === '/policies') {
      setSelectedPage('policies');
    } else if (location.pathname === '/book') {
      setSelectedPage('book');
    } else if (location.pathname === '/jokgu') {
      setSelectedPage('jokgu');
    }
  }, [location]);

  if (!user || !isLoggedIn) {
    return <></>;
  }

  return (
    <AppBar
      id="app-bar"
      enableColorOnDark
      position="fixed"
      sx={{
        boxShadow: 0,
        bgcolor: 'white',
        backgroundImage: 'none',
        color: 'black',
        maxWidth: '100vw',
      }}
    >
      <Container
        maxWidth="false"
        sx={{
          '&.MuiContainer-root': {
            borderBottom: '0.5px solid rgba(63, 129, 30, 0.25)',
            boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.1)',
            px: '0',
          },
        }}
      >
        <Toolbar
          disableGutters
          sx={{ display: { xs: 'none', sm: 'flex' }, height: '60px' }}
          variant="dense"
        >
          <Box
            sx={{
              '&.MuiBox-root': {
                alignItems: 'center',
                display: 'flex',
                flexGrow: 1,
                gap: {
                  xs: 0,
                  sm: 1,
                  md: 5,
                },
                px: 0,
              },
            }}
          >
            <Link
              to="/profile"
              style={{ alignItems: 'center', display: 'flex' }}
            >
              <img
                alt="Dumbbell Image"
                src="/fitness_center.svg"
                style={{
                  cursor: 'pointer',
                  height: '36.5px',
                  margin: '0 9px',
                }}
              />
            </Link>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mx: 'auto',
                gap: {
                  xs: 0,
                  sm: 1,
                  md: 5,
                },
              }}
            >
              <StyledButton
                className={selectedPage === 'profile' ? 'active' : 'profile'}
                onClick={() => {
                  navigate('/profile');
                  selectPage('profile');
                }}
                variant="text"
              >
                Profile
              </StyledButton>
              <StyledButton
                className={
                  selectedPage === 'agreement' ? 'active' : 'agreement'
                }
                onClick={() => {
                  navigate('/agreement');
                  selectPage('agreement');
                }}
                variant="text"
                sx={{ color: '#e65100' }}
              >
                Agreement
              </StyledButton>
              <StyledButton
                className={selectedPage === 'policies' ? 'active' : 'policies'}
                onClick={() => {
                  navigate('/policies');
                  selectPage('policies');
                }}
                variant="text"
              >
                Policies
              </StyledButton>
              <StyledButton
                className={selectedPage === 'book' ? 'active' : 'book'}
                onClick={() => {
                  navigate('/book');
                  selectPage('book');
                }}
                variant="text"
                disabled={!user?.waiverSigned}
              >
                Book
              </StyledButton>
              <StyledButton
                className={selectedPage === 'jokgu' ? 'active' : 'jokgu'}
                onClick={() => {
                  navigate('/jokgu');
                  selectPage('jokgu');
                }}
                variant="text"
                disabled={!user?.waiverSigned}
              >
                Jokgu👟
              </StyledButton>
            </Box>
          </Box>
        </Toolbar>
        <Box
          sx={{
            display: {
              xs: 'flex',
              sm: 'none',
            },
            justifyContent: 'center',
            alignContent: 'center',
          }}
        >
          <IconButton
            onClick={toggleDrawer(true)}
            ref={iconButtonRef}
            sx={{ marginLeft: 'auto' }}
            aria-label="Menu button"
          >
            <MenuIcon />
          </IconButton>
          <Drawer
            anchor="top"
            onClose={toggleDrawer(false)}
            open={toggleOpen}
            sx={{
              '& .MuiDrawer-paper': {
                width: '175px',
                marginLeft: 'auto',
                marginRight: '0',
              },
            }}
          >
            <Box
              sx={{
                bgcolor: 'white',
                p: 2,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <IconButton onClick={toggleDrawer(false)}>
                  <CloseRoundedIcon />
                </IconButton>
              </Box>
              <StyledMenuItem
                className={selectedPage === 'profile' ? 'active' : 'profile'}
                onClick={() => {
                  navigate('/profile');
                  selectPage('profile');
                  setToggleOpen(false);
                }}
              >
                Profile
              </StyledMenuItem>
              <StyledMenuItem
                className={
                  selectedPage === 'agreement' ? 'active' : 'agreement'
                }
                onClick={() => {
                  navigate('/agreement');
                  selectPage('agreement');
                  setToggleOpen(false);
                }}
                sx={{ color: '#e65100' }}
              >
                Agreement
              </StyledMenuItem>
              <StyledMenuItem
                className={selectedPage === 'policies' ? 'active' : 'policies'}
                onClick={() => {
                  navigate('/policies');
                  selectPage('policies');
                  setToggleOpen(false);
                }}
              >
                Policies
              </StyledMenuItem>
              <StyledMenuItem
                className={selectedPage === 'book' ? 'active' : 'book'}
                disabled={!user?.waiverSigned}
                onClick={() => {
                  navigate('/book');
                  selectPage('book');
                  setToggleOpen(false);
                }}
              >
                Book
              </StyledMenuItem>
              <StyledMenuItem
                className={selectedPage === 'jokgu' ? 'active' : 'jokgu'}
                disabled={!user?.waiverSigned}
                onClick={() => {
                  navigate('/jokgu');
                  selectPage('jokgu');
                  setToggleOpen(false);
                }}
              >
                Jokgu👟
              </StyledMenuItem>
            </Box>
          </Drawer>
        </Box>
      </Container>
    </AppBar>
  );
}
